import {classNames, observable, React} from '@api'
import {globalStores} from '@stores'
import {Layout, BorderNode, I18nLabel, IJsonTabNode, ILayoutMetrics, TabNode, Model, Rect, FlexLayoutNode, IDraggable, Action, Actions, DockLocation, DragDrop, CLASSES, TabSetNode} from '@flexlayout'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {action, autorun, computed, runInAction, trace, when} from 'mobx'
import {IDisposer} from 'mobx-utils'
import {FloatingTabsStore, SplittersStore, BordersStore, TabsStore, IIcons, ITabSetRenderValues, ITabRenderValues, TabSetsStore, ILayoutCallbacks, ILayoutProps} from './'

export class LayoutStore implements ILayoutCallbacks {
  floatingTabs: FloatingTabsStore
  splitters: SplittersStore
  borders: BordersStore
  tabs: TabsStore
  tabSets: TabSetsStore

  constructor(public layout: Layout) {
    // console.debug(`Construct layout`)
    this.floatingTabs = new FloatingTabsStore(this)
    this.splitters = new SplittersStore(this)
    this.borders = new BordersStore(this)
    this.tabs = new TabsStore(this)
    this.tabSets = new TabSetsStore(this)

    const {props} = layout

    this.tabIds = []
    this.selfRef = React.createRef<HTMLDivElement>()
    this.findHeaderBarSizeRef = React.createRef<HTMLDivElement>()
    this.findTabBarSizeRef = React.createRef<HTMLDivElement>()
    this.findBorderBarSizeRef = React.createRef<HTMLDivElement>()
    this.supportsPopout = props.supportsPopout !== undefined ? props.supportsPopout : true
    this.popoutURL = props.popoutURL ? props.popoutURL : 'popout.html'
    // For backwards compatibility, prop closeIcon sets prop icons.close:
    this.icons = props.closeIcon ? Object.assign({close: props.closeIcon}, props.icons) : props.icons
    this.firstRender = true
  }

  registerEffects = () => {
    this.props.model._setChangeListener(this.onModelChange)

    this._toDispose = [
      autorun(() => {
        const {model} = this
        if (model?._idMap) {
          runInAction(() => {
            this.asyncDoLayout()
            this.props.onModelChange && this.props.onModelChange(model)
          })
        }
      }),
      autorun(() => {
        const {rect} = this
        this.asyncDoLayout()
      }),
      when(() => this.selfRef != null, () => {
        this.firstRender = false
      })
    ]

    return this.dispose
  }

  @action dispose = () => {
    this._toDispose.forEach(f => f())
  }

  @computed get realtimeResize() { return this.props.realtimeResize}

  @computed get visibleTabs() {
    return _.orderBy(_.values(this.visibleTabsById), t => t.id)
  }

  @computed get visibleTabsById() {
    const {tabs: {tabs}, model: {flexLayout: {currentReport}}} = this
    return _.keyBy(tabs.filter(t => t.everVisible || t.isVisible || currentReport?.aboutToSubmitPdfJob), t => t.id) // (t.parent instanceof TabSetNode && t.is ) || (t.parent instanceof BorderNode && t.parent.isShowing && t.isVisible))
  }

  @observable props : Partial<ILayoutProps>

  /** @hidden @internal */
  @observable selfRef: React.RefObject<HTMLDivElement>
  /** @hidden @internal */
  @observable findHeaderBarSizeRef: React.RefObject<HTMLDivElement>
  /** @hidden @internal */
  @observable findTabBarSizeRef: React.RefObject<HTMLDivElement>
  /** @hidden @internal */
  @observable findBorderBarSizeRef: React.RefObject<HTMLDivElement>
  /** @hidden @internal */
  previousModel?: Model
  /** @hidden @internal */
  // Gets set in layout render pass
  @observable centerRect?: Rect

  /** @hidden @internal */
  // start: number = 0;
  /** @hidden @internal */
  // layoutTime: number = 0;

  /** @hidden @internal */
  tabIds: string[]
  /** @hidden @internal */
  newTabJson: any
  /** @hidden @internal */
  firstMove: boolean = false
  /** @hidden @internal */
  @observable dragNode?: FlexLayoutNode & IDraggable
  /** @hidden @internal */
  @observable dragDiv?: HTMLDivElement
  /** @hidden @internal */
  @observable dragDivText: string = ''
  /** @hidden @internal */
  @observable dropInfo: any
  /** @hidden @internal */
  @observable outlineDiv?: HTMLDivElement

  /** @hidden @internal */
  @observable edgeRightDiv?: HTMLDivElement
  /** @hidden @internal */
  @observable edgeBottomDiv?: HTMLDivElement
  /** @hidden @internal */
  @observable edgeLeftDiv?: HTMLDivElement
  /** @hidden @internal */
  @observable edgeTopDiv?: HTMLDivElement
  /** @hidden @internal */
  fnNewNodeDropped?: (node?: FlexLayoutNode, event?: Event) => void
  /** @hidden @internal */
  @observable currentDocument?: HTMLDocument
  /** @hidden @internal */
  @observable currentWindow?: Window
  /** @hidden @internal */
  supportsPopout: boolean
  /** @hidden @internal */
  @observable popoutURL: string
  /** @hidden @internal */
  icons?: IIcons
  /** @hidden @internal */
  @observable firstRender: boolean
  /** @hidden @internal */
  resizeObserver?: ResizeObserver

  @observable rect = new Rect(0, 0, 0, 0)
  @observable calculatedHeaderBarSize = 25
  @observable calculatedTabBarSize = 26
  @observable calculatedBorderBarSize = 30

  /** @hidden @internal */
  @action updateRect = (knownRect?: {width, height}) => {
    let rect = knownRect ?? this.domRect
    if (rect) {
      const newRect = new Rect(0, 0, rect.width, rect.height)
      if (!newRect.equals(this.rect) && rect.width !== 0 && rect.height !== 0) {
        this.rect = newRect
      }
    }
  }

  /** @hidden @internal */
  updateLayoutMetrics = () => {
    if (this.findHeaderBarSizeRef.current) {
      const headerBarSize = this.findHeaderBarSizeRef.current.getBoundingClientRect().height
      if (headerBarSize !== this.calculatedHeaderBarSize) {
        this.calculatedHeaderBarSize = headerBarSize
      }
    }
    if (this.findTabBarSizeRef.current) {
      const tabBarSize = this.findTabBarSizeRef.current.getBoundingClientRect().height
      if (tabBarSize !== this.calculatedTabBarSize) {
        this.calculatedTabBarSize = tabBarSize
      }
    }
    if (this.findBorderBarSizeRef.current) {
      const borderBarSize = this.findBorderBarSizeRef.current.getBoundingClientRect().height
      if (borderBarSize !== this.calculatedBorderBarSize) {
        this.calculatedBorderBarSize = borderBarSize
      }
    }
  }

  /** @hidden @internal */
  getClassName = (defaultClassName: string) => {
    if (this.props.classNameMapper === undefined) {
      return defaultClassName
    } else {
      return this.props.classNameMapper(defaultClassName)
    }
  }

  /** @hidden @internal */
  get domRect() : Partial<DOMRect> {
    return this.selfRef.current?.getBoundingClientRect() ?? {x: 0, y: 0, width: 0, height: 0, left: 0, top: 0}
  }

  /** @hidden @internal */
  @computed get rootDiv() {
    return this.selfRef.current!
  }

  /** @hidden @internal */
  componentWillUnmount() {
    this.resizeObserver?.unobserve(this.selfRef.current!)
  }

  @observable editingTab: TabNode

  /** @hidden @internal */
  @action setEditingTab = (tabNode?: TabNode) => {
    this.editingTab = tabNode
  }

  /** @hidden @internal */
  styleFont = (style: Record<string, string>): Record<string, string> => {
    if (this.props.font) {
      if (this.props.font.size) {
        style.fontSize = this.props.font.size
      }
      if (this.props.font.family) {
        style.fontFamily = this.props.font.family
      }
      if (this.props.font.style) {
        style.fontStyle = this.props.font.style
      }
      if (this.props.font.weight) {
        style.fontWeight = this.props.font.weight
      }
    }
    return style
  }

  /** @hidden @internal */
  @action onModelChange = (action?: Action) => {
    // if (action?.type !== Actions.SELECT_TAB) {
    //   console.log(`onModelChange - Full Rerender(!)`)
    //   this.layout.forceUpdate()  // Get rid of this once we're 100% mobx
    // }

    if (_.includes([Actions.ADJUST_BORDER_SPLIT, Actions.ADJUST_SPLIT], action?.type)) {
      this.layout.forceUpdate()
    }
    // else {
    //     console.log(`Skip force update `, action)
    // }

    if (this.props.onModelChange) {
      this.props.onModelChange(this.props.model)
    }

    this.asyncDoLayout()
  }

  /** @hidden @internal */
  @action doAction = (action: Action): FlexLayoutNode | undefined => {
    if (this.props.onAction !== undefined) {
      const outcome = this.props.onAction(action)
      if (outcome !== undefined) {
        return this.props.model.doAction(outcome)
      } else {
        // console.debug(`Ignoring action due to override`)
      }
      return undefined
    } else {
      return this.props.model.doAction(action)
    }
  }

  /** @hidden @internal */
  _getScreenRect = (node: TabNode) => {
    const rect = node!.rect!.clone()
    const bodyRect: Partial<DOMRect> = this.selfRef.current!.getBoundingClientRect() ?? {left: 0, top: 0, width: 0, height: 0, x: 0, y: 0}
    const navHeight = Math.min(80, this.currentWindow!.outerHeight - this.currentWindow!.innerHeight)
    const navWidth = Math.min(80, this.currentWindow!.outerWidth - this.currentWindow!.innerWidth)
    rect.x = rect.x + bodyRect.x + this.currentWindow!.screenX + navWidth
    rect.y = rect.y + bodyRect.y + this.currentWindow!.screenY + navHeight
    return rect
  }

  /**
   * Adds a new tab to the given tabset
   * @param tabsetId the id of the tabset where the new tab will be added
   * @param json the json for the new tab node
   */
  @action addTabToTabSet = (tabsetId: string, json: IJsonTabNode) => {
    const tabsetNode = this.props.model.nodeById(tabsetId)
    if (tabsetNode !== undefined) {
      this.doAction(Actions.addNode(json, tabsetId, DockLocation.CENTER, -1))
    }
  }

  /**
   * Adds a new tab to the active tabset (if there is one)
   * @param json the json for the new tab node
   */
  @action addTabToActiveTabSet = (json: IJsonTabNode) => {
    const tabsetNode = this.props.model?.activeTabset
    if (tabsetNode !== undefined) {
      this.doAction(Actions.addNode(json, tabsetNode.id, DockLocation.CENTER, -1))
    }
  }

  /**
   * Adds a new tab by dragging a labeled panel to the drop location, dragging starts immediatelly
   * @param dragText the text to show on the drag panel
   * @param json the json for the new tab node
   * @param onDrop a callback to call when the drag is complete
   */
  @action addTabWithDragAndDrop = (dragText: string, json: IJsonTabNode, onDrop?: (node?: FlexLayoutNode, event?: Event) => void) => {
    this.fnNewNodeDropped = onDrop
    this.newTabJson = json
    this.dragStart(undefined, dragText, TabNode._fromJson(json, this.props.model, false), true, undefined, undefined)
  }

  get dragDropInstance() { return DragDrop.instance}

  /**
   * Adds a new tab by dragging a labeled panel to the drop location, dragging starts when you
   * mouse down on the panel
   *
   * @param dragText the text to show on the drag panel
   * @param json the json for the new tab node
   * @param onDrop a callback to call when the drag is complete
   */
  @action addTabWithDragAndDropIndirect = (dragText: string, json: IJsonTabNode, onDrop?: () => void) => {
    this.fnNewNodeDropped = onDrop
    this.newTabJson = json

    DragDrop.instance.addGlass(this.onCancelAdd)

    this.dragDivText = dragText
    this.dragDiv = this.currentDocument!.createElement('div')
    this.dragDiv.className = this.getClassName('flexlayout__drag_rect')
    this.dragDiv.innerHTML = this.dragDivText
    this.dragDiv.addEventListener('mousedown', this.onDragDivMouseDown)
    this.dragDiv.addEventListener('touchstart', this.onDragDivMouseDown)

    const r = new Rect(10, 10, 150, 50)
    r.centerInRect(this.rect)
    this.dragDiv.style.left = r.x + 'px'
    this.dragDiv.style.top = r.y + 'px'

    const rootdiv = this.selfRef.current
    rootdiv!.appendChild(this.dragDiv)
  }

  /** @hidden @internal */
  @action onCancelAdd = () => {
    const rootdiv = this.selfRef.current
    rootdiv!.removeChild(this.dragDiv!)
    this.dragDiv = undefined
    if (this.fnNewNodeDropped != null) {
      this.fnNewNodeDropped()
      this.fnNewNodeDropped = undefined
    }
    DragDrop.instance.hideGlass()
    this.newTabJson = undefined
  }

  /** @hidden @internal */
  @action onCancelDrag = (wasDragging: boolean) => {
    if (wasDragging) {
      const rootdiv = this.selfRef.current!

      try {
        rootdiv.removeChild(this.outlineDiv!)
      } catch (e) {}

      try {
        rootdiv.removeChild(this.dragDiv!)
      } catch (e) {}

      this.dragDiv = undefined
      this.hideEdges(rootdiv)
      if (this.fnNewNodeDropped != null) {
        this.fnNewNodeDropped()
        this.fnNewNodeDropped = undefined
      }
      DragDrop.instance.hideGlass()
      this.newTabJson = undefined
    }
  }

  /** @hidden @internal */
  @action onDragDivMouseDown = (event: Event) => {
    event.preventDefault()
    this.dragStart(event, this.dragDivText, TabNode._fromJson(this.newTabJson, this.props.model, false), true, undefined, undefined)
  }

  /** @hidden @internal */
  @action dragStart = (
    event: Event | React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement> | React.DragEvent<HTMLDivElement> | undefined,
    dragDivText: string,
    node: FlexLayoutNode & IDraggable,
    allowDrag: boolean,
    onClick?: (event: Event) => void,
    onDoubleClick?: (event: Event) => void
  ) => {
    if (this.props.model.maximizedTabset !== undefined || !allowDrag) {
      DragDrop.instance.startDrag(event, undefined, undefined, undefined, undefined, onClick, onDoubleClick, this.currentDocument, this.selfRef.current!)
    } else {
      this.dragNode = node
      this.dragDivText = dragDivText
      DragDrop.instance.startDrag(event, this.onDragStart, this.onDragMove, this.onDragEnd, this.onCancelDrag, onClick, onDoubleClick, this.currentDocument, this.selfRef.current!)
    }
  }

  /** @hidden @internal */
  @action onDragStart = () => {
    this.dropInfo = undefined
    const rootdiv = this.selfRef.current!
    this.outlineDiv = this.currentDocument!.createElement('div')
    this.outlineDiv.className = this.getClassName(CLASSES.FLEXLAYOUT__OUTLINE_RECT)
    this.outlineDiv.style.visibility = 'hidden'
    rootdiv.appendChild(this.outlineDiv)

    if (this.dragDiv == null) {
      this.dragDiv = this.currentDocument!.createElement('div')
      this.dragDiv.className = this.getClassName(CLASSES.FLEXLAYOUT__DRAG_RECT)
      this.dragDiv.innerHTML = this.dragDivText
      rootdiv.appendChild(this.dragDiv)
    }
    // add edge indicators
    this.showEdges(rootdiv)

    if (this.dragNode !== undefined && this.dragNode instanceof TabNode && this.dragNode.tabRect !== undefined) {
      this.dragNode.tabRect!.positionElement(this.outlineDiv)
    }
    this.firstMove = true

    return true
  }

  /** @hidden @internal */
  onDragMove = (event: React.MouseEvent<Element>) => {
    if (this.firstMove === false) {
      const speed = this.props.model._getAttribute('tabDragSpeed') as number
      this.outlineDiv!.style.transition = `top ${speed}s, left ${speed}s, width ${speed}s, height ${speed}s`
    }
    this.firstMove = false
    const clientRect = this.selfRef.current!.getBoundingClientRect()
    const pos = {
      x: event.clientX - clientRect.left,
      y: event.clientY - clientRect.top
    }

    this.dragDiv!.style.left = pos.x - this.dragDiv!.getBoundingClientRect().width / 2 + 'px'
    this.dragDiv!.style.top = pos.y + 5 + 'px'

    const dropInfo = this.props.model._findDropTargetNode(this.dragNode!, pos.x, pos.y)
    // console.log(`onDragMove`, {dropTargetNode: dropInfo, pos})
    if (dropInfo) {
      this.dropInfo = dropInfo
      this.outlineDiv!.className = this.getClassName(dropInfo.className)
      dropInfo.rect.positionElement(this.outlineDiv!)
      this.outlineDiv!.style.visibility = 'visible'
    }
  }

  /** @hidden @internal */
  onDragEnd = (event: Event) => {
    const rootdiv = this.selfRef.current!
    rootdiv.removeChild(this.outlineDiv!)
    rootdiv.removeChild(this.dragDiv!)
    this.dragDiv = undefined
    this.hideEdges(rootdiv)
    DragDrop.instance.hideGlass()

    if (this.dropInfo) {
      if (this.newTabJson !== undefined) {
        const newNode = this.doAction(Actions.addNode(this.newTabJson, this.dropInfo.node.id, this.dropInfo.location, this.dropInfo.index))

        if (this.fnNewNodeDropped != null) {
          this.fnNewNodeDropped(newNode, event)
          this.fnNewNodeDropped = undefined
        }
        this.newTabJson = undefined
      } else if (this.dragNode !== undefined) {
        this.doAction(Actions.moveNode(this.dragNode.id, this.dropInfo.node.id, this.dropInfo.location, this.dropInfo.index))
      }
    }
  }

  @computed get className() {
    const {props: {className}} = this
    return classNames(className, this.getClassName(CLASSES.FLEXLAYOUT__LAYOUT))
  }

  /** @hidden @internal */
  onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    // DragDrop keeps track of number of dragenters minus the number of
    // dragleaves. Only start a new drag if there isn't one already.
    if (DragDrop.instance.isDragging())
      return
    const drag = this.props.onExternalDrag!(event)
    if (drag) {
      // Mimic addTabWithDragAndDrop, but pass in DragEvent
      this.fnNewNodeDropped = drag.onDrop
      this.newTabJson = drag.json
      this.dragStart(event, drag.dragText, TabNode._fromJson(drag.json, this.props.model, false), true, undefined, undefined)
    }
  }

  /** @hidden @internal */
  @action showEdges = (rootdiv: HTMLElement) => {
    if (this.props.model.isEnableEdgeDock()) {
      const domRect = rootdiv.getBoundingClientRect()
      const r = this.centerRect!
      const size = 100
      const length = size + 'px'
      const radius = '50px'
      const width = '10px'

      this.edgeTopDiv = this.currentDocument!.createElement('div')
      this.edgeTopDiv.className = this.getClassName(CLASSES.FLEXLAYOUT__EDGE_RECT)
      this.edgeTopDiv.style.top = r.y + 'px'
      this.edgeTopDiv.style.left = r.x + (r.width - size) / 2 + 'px'
      this.edgeTopDiv.style.width = length
      this.edgeTopDiv.style.height = width
      this.edgeTopDiv.style.borderBottomLeftRadius = radius
      this.edgeTopDiv.style.borderBottomRightRadius = radius

      this.edgeLeftDiv = this.currentDocument!.createElement('div')
      this.edgeLeftDiv.className = this.getClassName(CLASSES.FLEXLAYOUT__EDGE_RECT)
      this.edgeLeftDiv.style.top = r.y + (r.height - size) / 2 + 'px'
      this.edgeLeftDiv.style.left = r.x + 'px'
      this.edgeLeftDiv.style.width = width
      this.edgeLeftDiv.style.height = length
      this.edgeLeftDiv.style.borderTopRightRadius = radius
      this.edgeLeftDiv.style.borderBottomRightRadius = radius

      this.edgeBottomDiv = this.currentDocument!.createElement('div')
      this.edgeBottomDiv.className = this.getClassName(CLASSES.FLEXLAYOUT__EDGE_RECT)
      this.edgeBottomDiv.style.bottom = domRect.height - r.bottom + 'px'
      this.edgeBottomDiv.style.left = r.x + (r.width - size) / 2 + 'px'
      this.edgeBottomDiv.style.width = length
      this.edgeBottomDiv.style.height = width
      this.edgeBottomDiv.style.borderTopLeftRadius = radius
      this.edgeBottomDiv.style.borderTopRightRadius = radius

      this.edgeRightDiv = this.currentDocument!.createElement('div')
      this.edgeRightDiv.className = this.getClassName(CLASSES.FLEXLAYOUT__EDGE_RECT)
      this.edgeRightDiv.style.top = r.y + (r.height - size) / 2 + 'px'
      this.edgeRightDiv.style.right = domRect.width - r.right + 'px'
      this.edgeRightDiv.style.width = width
      this.edgeRightDiv.style.height = length
      this.edgeRightDiv.style.borderTopLeftRadius = radius
      this.edgeRightDiv.style.borderBottomLeftRadius = radius

      rootdiv.appendChild(this.edgeTopDiv)
      rootdiv.appendChild(this.edgeLeftDiv)
      rootdiv.appendChild(this.edgeBottomDiv)
      rootdiv.appendChild(this.edgeRightDiv)
    }
  }

  /** @hidden @internal */
  @action hideEdges = (rootdiv: HTMLElement) => {
    if (this.props.model.isEnableEdgeDock()) {
      try {
        rootdiv.removeChild(this.edgeTopDiv!)
        rootdiv.removeChild(this.edgeLeftDiv!)
        rootdiv.removeChild(this.edgeBottomDiv!)
        rootdiv.removeChild(this.edgeRightDiv!)
      } catch (e) {}
    }
  }

  /** @hidden @internal */
  @action maximize = (tabSet: TabSetNode) => {
    globalStores.flexLayout.maximizeTabSet(tabSet.id)
    //this.doAction(Actions.maximizeToggle(tabsetNode.id));
  }

  /** @hidden @internal */
  @action customizeTab = (
    tabNode: TabNode,
    renderValues: ITabRenderValues
  ) => {
    if (this.props.onRenderTab) {
      this.props.onRenderTab(tabNode, renderValues)
    }
  }

  /** @hidden @internal */
  @action customizeTabSet = (
    tabSetNode: TabSetNode | BorderNode,
    renderValues: ITabSetRenderValues
  ) => {
    if (this.props.onRenderTabSet) {
      this.props.onRenderTabSet(tabSetNode, renderValues)
    }
  }

  /** @hidden @internal */
  i18nName = (id: I18nLabel, param?: string) => {
    let message
    if (this.props.i18nMapper) {
      message = this.props.i18nMapper(id, param)
    }
    if (message === undefined) {
      message = id + (param === undefined ? '' : param)
    }
    return message
  }

  @computed get metrics() {
    const metrics: ILayoutMetrics = {
      headerBarSize: this.calculatedHeaderBarSize,
      tabBarSize: this.calculatedTabBarSize,
      borderBarSize: this.calculatedBorderBarSize
    }

    return metrics
  }


  @computed get model() : Model { return this.props?.model}

  @observable hasDoneLayoutPass = false

  @action doLayout = () => {
    const {centerRect, metrics} = this
    const rect = this.props.model._layout(this.rect, metrics)
    if (!centerRect || !centerRect.equals(rect)) {
      runInAction(() => {
        this.centerRect = rect
      })
    }

    !this.hasDoneLayoutPass && setTimeout(() => this.hasDoneLayoutPass = true, 100)
  }

  asyncDoLayout = _.throttle(() => {
    this.doLayout()
  }, 20)

  _toDispose: IDisposer[] = []
}


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/view/Layout/LayoutStore.tsx